/**
 * ServiceCatalogPage — Issue #331.
 *
 * 服务目录管理：列表 + 新建 + 合规审核 + 启用/停用。
 * 通过 @jinzhuan/api-client 走真实后端，禁止前端伪状态。
 */
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  hasPermission,
  SERVICE_CATALOG_PERMISSIONS,
} from '@/lib/procurementPermissions';
import {
  useServiceCatalogList,
  useServiceCatalogActions,
  type CreateServiceCatalogDto,
  type ServiceCatalogSummaryDto,
} from '@/hooks/useServiceCatalog';

const COMPLIANCE_LABELS: Record<string, string> = {
  pending_review: '待审核',
  approved: '已批准',
  suspended: '已暂停',
  retired: '已退役',
};

const COMPLIANCE_COLORS: Record<string, string> = {
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  suspended: 'bg-orange-100 text-orange-700',
  retired: 'bg-gray-200 text-gray-500',
};

const CATEGORY_LABELS: Record<string, string> = {
  detection: '检测',
  manufacturing: '制备',
  infusion: '回输',
  followup: '随访',
  consultation: '咨询',
  other: '其他',
};

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        COMPLIANCE_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {COMPLIANCE_LABELS[status] ?? status}
    </span>
  );
}

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { create, loading, error } = useServiceCatalogActions();
  const [form, setForm] = useState<CreateServiceCatalogDto>({
    code: '',
    name: '',
    category: 'detection',
    unit: 'time',
    price: '',
    cost: '',
    currency: 'CNY',
  });
  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const ok = await create(form);
    if (ok) {
      onCreated();
      onClose();
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">新建服务目录</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">编码</label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">名称</label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">类别</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value as CreateServiceCatalogDto['category'],
                  })
                }
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">单位</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.unit}
                onChange={(e) =>
                  setForm({ ...form, unit: e.target.value as CreateServiceCatalogDto['unit'] })
                }
              >
                <option value="time">次</option>
                <option value="session">疗程</option>
                <option value="course">套餐</option>
                <option value="piece">件</option>
                <option value="mL">毫升</option>
                <option value="hour">小时</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">售价（元）</label>
              <input
                required
                type="text"
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]+)?"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">成本（元）</label>
              <input
                required
                type="text"
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]+)?"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">适用范围（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.applicableScope ?? ''}
              onChange={(e) =>
                setForm({ ...form, applicableScope: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">描述（可选）</label>
            <textarea
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              rows={2}
              value={form.description ?? ''}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value || undefined })
              }
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ServiceCatalogPage() {
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, SERVICE_CATALOG_PERMISSIONS.READ);
  const canCreate = hasPermission(
    permissions,
    SERVICE_CATALOG_PERMISSIONS.CREATE,
  );
  const canReview = hasPermission(
    permissions,
    SERVICE_CATALOG_PERMISSIONS.REVIEW,
  );
  const canUpdate = hasPermission(
    permissions,
    SERVICE_CATALOG_PERMISSIONS.UPDATE,
  );

  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, fetchList } = useServiceCatalogList();
  const {
    updateComplianceStatus,
    setEnabled,
    loading: actionLoading,
  } = useServiceCatalogActions();

  const refresh = () =>
    fetchList({
      complianceStatus: statusFilter || undefined,
      category: categoryFilter || undefined,
      search: search || undefined,
    });

  useEffect(() => {
    if (canRead) {
      void refresh();
    }
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看服务目录的权限。</div>
    );
  }

  async function onApprove(row: ServiceCatalogSummaryDto) {
    const ok = await updateComplianceStatus(row.id, {
      expectedVersion: row.version,
      complianceStatus: 'approved',
    });
    if (ok) void refresh();
  }
  async function onSuspend(row: ServiceCatalogSummaryDto) {
    const ok = await updateComplianceStatus(row.id, {
      expectedVersion: row.version,
      complianceStatus: 'suspended',
    });
    if (ok) void refresh();
  }
  async function onRetire(row: ServiceCatalogSummaryDto) {
    if (!window.confirm('确认退役该服务？退役后不可恢复。')) return;
    const ok = await updateComplianceStatus(row.id, {
      expectedVersion: row.version,
      complianceStatus: 'retired',
    });
    if (ok) void refresh();
  }
  async function onToggleEnable(row: ServiceCatalogSummaryDto) {
    const ok = await setEnabled(row.id, {
      expectedVersion: row.version,
      enabled: !row.enabled,
    });
    if (ok) void refresh();
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">服务目录</h1>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            新建服务
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">合规状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(COMPLIANCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">类别</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="编码或名称"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">编码</th>
              <th className="px-3 py-2 text-left">名称</th>
              <th className="px-3 py-2 text-left">类别</th>
              <th className="px-3 py-2 text-left">单位</th>
              <th className="px-3 py-2 text-right">售价</th>
              <th className="px-3 py-2 text-left">合规状态</th>
              <th className="px-3 py-2 text-left">启用</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                <td className="px-3 py-2">{row.name}</td>
                <td className="px-3 py-2">
                  {CATEGORY_LABELS[row.category] ?? row.category}
                </td>
                <td className="px-3 py-2">{row.unit}</td>
                <td className="px-3 py-2 text-right">{fmtCny(row.price)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.complianceStatus} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {row.enabled ? '已启用' : '未启用'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {canReview && row.complianceStatus !== 'retired' && (
                      <>
                        {row.complianceStatus !== 'approved' && (
                          <button
                            disabled={actionLoading}
                            onClick={() => void onApprove(row)}
                            className="rounded border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700"
                          >
                            批准
                          </button>
                        )}
                        {row.complianceStatus === 'approved' && (
                          <button
                            disabled={actionLoading}
                            onClick={() => void onSuspend(row)}
                            className="rounded border border-orange-300 bg-orange-50 px-2 py-0.5 text-xs text-orange-700"
                          >
                            暂停
                          </button>
                        )}
                        <button
                          disabled={actionLoading}
                          onClick={() => void onRetire(row)}
                          className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600"
                        >
                          退役
                        </button>
                      </>
                    )}
                    {canUpdate &&
                      row.complianceStatus === 'approved' &&
                      !row.enabled && (
                        <button
                          disabled={actionLoading}
                          onClick={() => void onToggleEnable(row)}
                          className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                        >
                          启用
                        </button>
                      )}
                    {canUpdate && row.enabled && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onToggleEnable(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                      >
                        停用
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  暂无服务目录
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateDialog
        key={createOpen ? 'open' : 'closed'}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refresh()}
      />
    </div>
  );
}
